import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import {
  formatVariantDisplayName,
  normalizeProductImages,
  primaryProductImageUrl,
} from '../../../../utils/productGroups'
import { formatDurationLabel } from '../../../../utils/rentalPeriod'

const FLY_DURATION_MS = 800

function parsePrice(value) {
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : NaN
}

function variantHasDiscount(variant) {
  const pid = variant?.promotionId
  if (pid == null || pid === '') return false
  const cur = parsePrice(variant?.price)
  const old = parsePrice(variant?.oldPrice ?? variant?.compareAtPrice)
  return Number.isFinite(cur) && Number.isFinite(old) && old > cur
}

function toCartProduct(group, variant) {
  const cartItem = {
    id: variant.id ?? variant._id,
    _id: variant.id ?? variant._id,
    name: formatVariantDisplayName(group?.name, variant?.title),
    price: variant.price,
    image: group?.image ?? group?.imageUrl ?? primaryProductImageUrl(group?.images),
    description: group?.description || '',
    durationMinutes: variant?.durationMinutes ?? null,
  }
  if (variantHasDiscount(variant) && variant?.promotionId != null && variant?.promotionId !== '') {
    cartItem.promotionId = variant.promotionId
    cartItem.oldPrice = variant.oldPrice
  }
  return cartItem
}

function getDefaultFabCenter() {
  if (typeof window === 'undefined') return { x: 100, y: 100 }
  const isMobile = window.innerWidth <= 768
  return {
    x: isMobile ? 50 : window.innerWidth - 100,
    y: window.innerHeight - 150,
  }
}

function readFabCenter() {
  const fab = document.querySelector('.floating-action-button')
  if (!fab) return null
  const rect = fab.getBoundingClientRect()
  if (rect.width === 0 && rect.height === 0) return null
  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2,
  }
}

async function resolveFlyTarget() {
  await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)))
  let target = readFabCenter()
  if (target) return target
  await new Promise((r) => setTimeout(r, 80))
  target = readFabCenter()
  return target ?? getDefaultFabCenter()
}

/**
 * Inline product purchase logic for the linear view: variant selection,
 * pricing for the selected variant, and add-to-cart with a fly bubble.
 * Self-contained — no detail modal, no dependency on useProductCard.
 */
export function useLinearProduct(product, { onAddToCart, cartCount = 0 } = {}) {
  const images = useMemo(
    () => normalizeProductImages(product?.images, product?.image ?? product?.imageUrl),
    [product]
  )
  const coverImage = primaryProductImageUrl(images, product?.image ?? product?.imageUrl)

  const variants = useMemo(
    () => (Array.isArray(product?.variants) ? product.variants : []),
    [product]
  )

  const [selectedVariantId, setSelectedVariantId] = useState(
    () => variants[0]?.id ?? variants[0]?._id ?? null
  )
  const [isAnimating, setIsAnimating] = useState(false)
  const [flyItems, setFlyItems] = useState([])
  const flyIdRef = useRef(0)

  useEffect(() => {
    setSelectedVariantId(variants[0]?.id ?? variants[0]?._id ?? null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id, product?._id])

  const selectedVariant = useMemo(() => {
    const found = variants.find((v) => (v.id ?? v._id) === selectedVariantId)
    return found ?? variants[0] ?? null
  }, [variants, selectedVariantId])

  const pricing = useMemo(() => {
    if (selectedVariant) {
      const current = parsePrice(selectedVariant?.price)
      const old = parsePrice(selectedVariant?.oldPrice ?? selectedVariant?.compareAtPrice)
      const showOld = Number.isFinite(current) && Number.isFinite(old) && old > current
      const label = formatDurationLabel(selectedVariant?.durationMinutes)
      return {
        currentPrice: current,
        oldPrice: showOld ? old : null,
        pricePrefix: '',
        priceSuffix: label ? ` / ${label}` : '',
      }
    }
    return { currentPrice: NaN, oldPrice: null, pricePrefix: '', priceSuffix: '' }
  }, [selectedVariant])

  const spawnFlyToCart = useCallback(async (fromX, fromY) => {
    let target = await resolveFlyTarget()
    if (Math.hypot(target.x - fromX, target.y - fromY) < 48) {
      target = getDefaultFabCenter()
    }
    const id = ++flyIdRef.current
    const dx = target.x - fromX
    const dy = target.y - fromY

    setFlyItems((prev) => [
      ...prev,
      { id, fromX, fromY, dx, dy, image: coverImage },
    ])

    window.setTimeout(() => {
      setFlyItems((prev) => prev.filter((item) => item.id !== id))
      window.dispatchEvent(new CustomEvent('shops:cart-fly-landed'))
    }, FLY_DURATION_MS)
  }, [coverImage])

  const handleAddToCartClick = useCallback((e) => {
    if (!selectedVariant) return
    const buttonRect = e.currentTarget.getBoundingClientRect()
    const origin = {
      x: buttonRect.left + buttonRect.width / 2,
      y: buttonRect.top + buttonRect.height / 2,
    }

    setIsAnimating(true)
    if (cartCount > 0) {
      spawnFlyToCart(origin.x, origin.y)
    }
    if (onAddToCart) {
      onAddToCart(toCartProduct(product, selectedVariant), origin)
    }
    window.setTimeout(() => {
      setIsAnimating(false)
    }, FLY_DURATION_MS)
  }, [cartCount, onAddToCart, product, selectedVariant, spawnFlyToCart])

  return {
    images,
    coverImage,
    variants,
    selectedVariantId,
    selectVariant: setSelectedVariantId,
    selectedVariant,
    pricing,
    isAnimating,
    handleAddToCartClick,
    flyItems,
  }
}
